/**
 * perkie-pdp-d23.js — port step 38 (D23): the PDP desktop composition,
 * 750px and up. Loaded (deferred) by sections/main-product.liquid only when
 * the section carries the perkie_previewer block, i.e. on
 * templates/product.custom.json. Below 750 every element this file touches
 * is display:none (perkie-pdp-d23.css) except the personal line under the
 * H1, shown below 750 since port step 39 (D24 Q6) and only once a preview
 * exists, so the 390 layout before an upload is unchanged.
 *
 * Canon: redesign-brief/canon-export-2026-08-09/Desktop Compositions - PDP
 * and Cart Page.dc.html (DESIGN FINAL 2026-10-07, v1791416884291123,
 * md5 e737a847…; the ≤1440 frames are unchanged from the 10-06 FINAL).
 *
 * What it does
 *  - R1 handover: the first finished preview fades into the plate box the
 *    product photo held (450ms; none under prefers-reduced-motion) and a
 *    "Your art" tile is prepended to the strip and selected. A product
 *    thumbnail swaps the plate back to that photo (Dawn's own media-gallery
 *    does the swap); "Your art" brings the art back. Nothing below moves:
 *    the art is an overlay on the same box.
 *  - The plate caption, as D23 draws it: "BELLA · INK WASH · PREVIEW" at
 *    the handover (the style of the art shown, from flow3c-state's
 *    previewStyleLabel), "BELLA · INK WASH" once a style is chosen,
 *    "BELLA + OLLIE · INK WASH" for two pets (uppercase is CSS).
 *  - A style choice crossfades the plate to that style's art.
 *  - The personal line under the H1 ("of Bella" / "of Bella, Ink Wash" /
 *    "of Bella and Ollie, Ink Wash"; Port Step 45 FP1, C7: the title reads
 *    "Framed Perkie Print of Bella"). Shown from the moment the wait
 *    starts (flow3c-state step 4), before any tile can be tapped (FP1 A4).
 *  - Port Step 48-E (Corey, PM-298 A.2): the promise block's fix line ("If it
 *    doesn't look like {Name}, tell us, ..."; standard pages only, every
 *    width) takes the same names, joined the same way, once every pet has
 *    one; "your pet" (the Liquid's) otherwise. Port Step 48-F (Corey,
 *    PM-300): so does Deb's memorial fix line (memorial pages and
 *    ?context=memorial sessions; "them" with no name), and two or more pets
 *    not all named read "your pets" on the standard line. The no-name words
 *    are each span's own data attributes (main-product.liquid).
 *  Money: nothing. Ruling A (Corey 2026-10-07): the 3C card owns the price
 *  at every width; the buy column shows Dawn's product price only.
 *  - BET 01 state-gated pinning: the block from the pickers through Send as
 *    a Gift (pin scope ruled (a), Corey 2026-10-07) pins at
 *    top 112px once a style is chosen, only while it fits the viewport.
 *  From 750 (Corey 2026-10-11; step 38 drew it above 1440 only):
 *  - The image run replaces the plate and strip (CSS). The art overlay
 *    covers the lead tile only; "Your art" / "Photo 1" chips on the lead
 *    tile swap it with the displaced product shot (FLAG D23-07).
 *  Above 1440 (the wide state, step 38 wide addendum):
 *  - FLAG D23-08 (Corey 2026-10-07): before a style is chosen the whole buy
 *    column is sticky at top = min(112, viewportH - columnH - 16), so it
 *    scrolls until Send as a Gift meets the window bottom, then holds.
 *    Recomputed on resize and whenever the column's height changes. Once a
 *    style is chosen the column goes static and the BET 01 pin takes over.
 *  - "Show N more photos" loads the run's photos 10+ into the same grid
 *    (Corey 2026-10-11; it used to open Dawn's full-screen modal).
 *
 * Events (READ ONLY; this file dispatches nothing):
 *  - perkie:flow3c-state  (bubbles from the 3C card root; detail.previewUrl,
 *    detail.names (Q2) and detail.previewStyle / previewStyleLabel
 *    (ruling B) added in step 38, approved 2026-10-07)
 *  - perkie:progress      (window; stage "ready" = a style is chosen)
 *  - perkie:style-selected (window; pets[].previewUrl, combinedPreviewUrl,
 *    pets[].name, styleLabel)
 *  - perkie:previewer-reset (window)
 * Other listeners on the same types (Gate 1 event map, 2026-10-07):
 * assets/perkie-cart-bridge.js (style-selected, previewer-reset, progress),
 * snippets/perkie-sticky-atc.liquid (style-selected, previewer-reset),
 * snippets/perkie-gift-this-button.liquid (progress, previewer-reset),
 * sections/perkie-reveal-hero.liquid (flow3c-state; free-preview page only).
 * Multiple listeners per perkie:* type are the intended design: this file
 * never proxies addEventListener and never stops propagation. Its only
 * double-load guard is the in-file flag below.
 *
 * Load order: this <script defer> sits near the top of <product-info>, so it
 * executes BEFORE the deferred perkie-previewer.js (later in the document)
 * mounts the card; no mount-time dispatch is missed.
 */
(function () {
  "use strict";
  if (window.__perkieD23Loaded) return;
  window.__perkieD23Loaded = true;

  var root = document.querySelector("product-info.perkie-d23");
  if (!root) return;

  var PIN_TOP = 112;
  var HOLD_GAP = 16;
  var wideMql = window.matchMedia ? window.matchMedia("(min-width: 750px)") : null;
  function isWide() { return !!(wideMql && wideMql.matches); }
  // The wide state (above 1440): image run, swap chips, D23-08 column hold.
  var xwMql = window.matchMedia ? window.matchMedia("(min-width: 1441px)") : null;
  function isXWide() { return !!(xwMql && xwMql.matches); }

  var gallery = root.querySelector("media-gallery");
  var mount = root.querySelector('[data-perkie-previewer="product"]');
  var pin = root.querySelector("[data-perkie-d23-pin]");
  var personal = root.querySelector("[data-perkie-d23-personal]");
  var column = root.querySelector(".product__info-wrapper");
  var seeAll = root.querySelector("[data-perkie-d23-seeall]");
  // Port Step 48-E (Corey, PM-298 A.2): the {Name} in the promise block's fix
  // line (standard pages only; main-product.liquid renders "your pet").
  // Port Step 48-F (Corey, PM-300): the memorial block's fix line too (Deb's;
  // the Liquid renders "them"). Each span's no-name words come from its own
  // markup, read once here, so this file holds no copy:
  // data-perkie-d23-fix-name (standard; one pet, or no state yet) or
  // data-perkie-d23-fix-name-mem (memorial; its own attribute so 48-E's
  // script could never reach it), and data-perkie-d23-fix-many (two or more
  // pets, standard only); a span without them keeps the Liquid's text.
  var fixNames = [];
  (function () {
    var els = root.querySelectorAll("[data-perkie-d23-fix-name], [data-perkie-d23-fix-name-mem]");
    for (var i = 0; i < els.length; i++) {
      var one = els[i].getAttribute("data-perkie-d23-fix-name") || els[i].getAttribute("data-perkie-d23-fix-name-mem") || els[i].textContent;
      fixNames.push({ el: els[i], one: one, many: els[i].getAttribute("data-perkie-d23-fix-many") || one });
    }
  })();

  // art: the card's current/first-ready art (flow3c-state); artLabel: the
  // Title Case style of that art. names: as typed.
  // chosen: the last perkie:style-selected detail. stage: last perkie:progress.
  // showArt: the plate shows the art (true) or Dawn's product media (false).
  var s = { art: null, artLabel: "", names: [], chosen: null, stage: null, showArt: false, step: 0 };

  /* ── helpers ─────────────────────────────────────────────────────── */
  function trimAll(list) {
    var out = [];
    for (var i = 0; i < list.length; i++) out.push(String(list[i] == null ? "" : list[i]).trim());
    return out;
  }
  // "Bella", "Bella and Ollie", "Bella, Ollie and Pip" (the card's listNames).
  function listNames(l) {
    if (l.length < 3) return l.join(" and ");
    return l.slice(0, -1).join(", ") + " and " + l[l.length - 1];
  }
  function isChosen() { return !!(s.chosen && s.stage === "ready"); }
  function chosenArt() {
    var d = s.chosen;
    if (!d) return null;
    if (d.combinedPreviewUrl) return d.combinedPreviewUrl;
    var p = d.pets && d.pets[0];
    return (p && p.previewUrl) || null;
  }
  function currentNames() {
    if (isChosen() && s.chosen.pets && s.chosen.pets.length) {
      var n = [];
      for (var i = 0; i < s.chosen.pets.length; i++) n.push(s.chosen.pets[i] && s.chosen.pets[i].name);
      return trimAll(n);
    }
    return trimAll(s.names || []);
  }
  // Every pet named, or no names at all (the line is absent, never "Pet 1").
  function namedAll(n) {
    if (!n.length) return false;
    for (var i = 0; i < n.length; i++) if (!n[i]) return false;
    return true;
  }

  /* ── the plate (an overlay on Dawn's viewer: same box, nothing moves) ─ */
  var plate = null, layers = [], front = 0, cap = null;
  function viewer() { return gallery && gallery.querySelector('[id^="GalleryViewer"]'); }
  function thumbList() { return gallery && gallery.querySelector('[id^="Slider-Thumbnails"]'); }

  function ensurePlate() {
    var v = viewer();
    if (!v) return null;
    if (plate && plate.parentNode === v) return plate;
    if (!plate) {
      plate = document.createElement("div");
      plate.className = "perkie-d23-plate";
      plate.setAttribute("aria-hidden", "true");
      for (var i = 0; i < 2; i++) {
        var img = document.createElement("img");
        img.className = "perkie-d23-plate__art";
        img.alt = "";
        img.decoding = "async";
        plate.appendChild(img);
        layers.push(img);
      }
      cap = document.createElement("span");
      cap.className = "perkie-d23-plate__cap";
      plate.appendChild(cap);
    }
    v.appendChild(plate);
    return plate;
  }

  function setPlateArt(url) {
    if (!ensurePlate() || !url) return;
    var cur = layers[front];
    if (cur.getAttribute("src") === url) return;
    if (!cur.getAttribute("src")) {
      cur.src = url;
      cur.classList.add("is-front");
      cur.alt = "Your art";
      return;
    }
    // Crossfade art to art: load into the back layer, then swap.
    var back = layers[1 - front];
    var swap = function () {
      back.classList.add("is-front");
      back.alt = "Your art";
      cur.classList.remove("is-front");
      cur.alt = "";
      front = 1 - front;
    };
    back.onload = null;
    back.src = url;
    if (back.complete && back.naturalWidth) swap();
    else back.onload = function () { back.onload = null; if (back.getAttribute("src") === url) swap(); };
  }

  /* ── wide: "Your art" / "Photo 1" on the lead tile (FLAG D23-07) ──── */
  var swapEl = null;
  function makeChip(label, toArt) {
    var b = document.createElement("button");
    b.type = "button";
    b.className = "perkie-d23-swap__chip";
    b.textContent = label;
    b.setAttribute("aria-pressed", "false");
    b.addEventListener("click", function () {
      s.showArt = toArt;
      render();
    });
    return b;
  }
  function ensureSwap() {
    var v = viewer();
    if (!v) return null;
    if (!swapEl) {
      swapEl = document.createElement("div");
      swapEl.className = "perkie-d23-swap";
      swapEl.setAttribute("data-perkie-d23-swap", "");
      swapEl.hidden = true;
      swapEl.appendChild(makeChip("Your art", true));
      swapEl.appendChild(makeChip("Photo 1", false));
    }
    if (swapEl.parentNode !== v) v.appendChild(swapEl);
    return swapEl;
  }
  function renderSwap(art, artOn) {
    // Rendered only when art exists; CSS shows it from 750 (the run's lead tile).
    if (!art && !swapEl) return;
    var el = ensureSwap();
    if (!el) return;
    if (el.hidden !== !art) el.hidden = !art;
    var chips = el.children;
    var want = [artOn ? "true" : "false", artOn ? "false" : "true"];
    for (var i = 0; i < chips.length; i++) {
      if (chips[i].getAttribute("aria-pressed") !== want[i]) chips[i].setAttribute("aria-pressed", want[i]);
    }
  }

  /* ── the "Your art" tile, first in Dawn's strip ──────────────────── */
  function ensureTile(url) {
    var list = thumbList();
    if (!list) return null;
    var li = list.querySelector("[data-perkie-d23-art-thumb]");
    if (!url) {
      if (li && li.parentNode) li.parentNode.removeChild(li);
      return null;
    }
    if (!li) {
      li = document.createElement("li");
      li.className = "thumbnail-list__item perkie-d23-art-thumb";
      li.setAttribute("data-perkie-d23-art-thumb", "");
      var b = document.createElement("button");
      b.type = "button";
      b.className = "thumbnail perkie-d23-art-thumb__btn";
      b.setAttribute("aria-label", "Your art");
      var im = document.createElement("img");
      im.alt = "";
      im.decoding = "async";
      b.appendChild(im);
      li.appendChild(b);
    }
    if (list.firstElementChild !== li) list.insertBefore(li, list.firstElementChild);
    var img = li.querySelector("img");
    if (img && img.getAttribute("src") !== url) img.src = url;
    return li;
  }

  // aria-current mirrors what the plate shows: the art tile while the art is
  // up, otherwise the thumbnail of Dawn's active media.
  function syncSelection(artOn) {
    var list = thumbList();
    if (!list) return;
    var li = list.querySelector("[data-perkie-d23-art-thumb]");
    var tileBtn = li && li.querySelector("button");
    var i, cur;
    if (artOn && tileBtn) {
      if (tileBtn.getAttribute("aria-current") !== "true") tileBtn.setAttribute("aria-current", "true");
      cur = list.querySelectorAll("[data-target] button[aria-current]");
      for (i = 0; i < cur.length; i++) cur[i].removeAttribute("aria-current");
      return;
    }
    if (tileBtn && tileBtn.hasAttribute("aria-current")) tileBtn.removeAttribute("aria-current");
    if (!list.querySelector("[data-target] button[aria-current]")) {
      var v = viewer();
      var active = v && v.querySelector(".product__media-item.is-active");
      var id = active && active.getAttribute("data-media-id");
      var items = list.querySelectorAll("[data-target]");
      for (i = 0; i < items.length; i++) {
        if (items[i].getAttribute("data-target") === id) {
          var btn = items[i].querySelector("button");
          if (btn) btn.setAttribute("aria-current", "true");
          break;
        }
      }
    }
  }

  /* ── below 750: a thin position bar under the swipe slider ─────────── */
  // Corey 2026-10-11 (the abercrombie.com bar, in place of Dawn's "1 / N"):
  // the dark segment is the slider's visible share of the run and moves with
  // the scroll. It is laid over the bottom of the current photo: placed from
  // the first slide's photo box (all slides share it), 12px in from its
  // sides and bottom. Hidden by CSS from 750; hidden for one photo.
  var bar = null, barList = null;
  function paintBar() {
    var list = barList;
    if (!list || !bar) return;
    var sw = list.scrollWidth, cw = list.clientWidth;
    var f = sw > 0 ? Math.min(1, cw / sw) : 1;
    var seg = bar.firstChild;
    seg.style.width = (f * 100) + "%";
    seg.style.transform = "translateX(" + (cw > 0 ? (list.scrollLeft / cw) * 100 : 0) + "%)";
    var hide = f >= 0.999;
    if (bar.hidden !== hide) bar.hidden = hide;
    var v = bar.parentNode;
    var box = list.querySelector(".product-media-container");
    if (hide || !v || !box || !box.offsetWidth) return;
    var vr = v.getBoundingClientRect(), br = box.getBoundingClientRect();
    // the first photo box as it sits at scroll 0 = where the current one snaps
    bar.style.left = Math.round(br.left - vr.left + list.scrollLeft + 12) + "px";
    bar.style.right = "auto";
    bar.style.width = Math.max(0, Math.round(br.width - 24)) + "px";
    bar.style.top = Math.round(br.bottom - vr.top - 14) + "px";
    bar.style.bottom = "auto";
  }
  function syncBar() {
    var v = viewer();
    var list = v && v.querySelector(".product__media-list");
    if (!list) return;
    if (!bar) {
      bar = document.createElement("div");
      bar.className = "perkie-d23-bar";
      bar.setAttribute("aria-hidden", "true");
      bar.appendChild(document.createElement("span"));
    }
    if (bar.parentNode !== v) v.appendChild(bar);
    if (barList !== list) {
      if (barList) barList.removeEventListener("scroll", paintBar);
      list.addEventListener("scroll", paintBar, { passive: true });
      barList = list;
    }
    paintBar();
  }

  /* ── 750+: the full-size view gets a thumbnail rail (abercrombie.com) ── */
  // Corey 2026-10-11. Dawn's product media modal (moved to <body> by Dawn)
  // is marked .perkie-d23-lightbox and given a fixed column of thumbnails,
  // one per photo the modal shows (hide_variants photos excluded). A
  // thumbnail scrolls its photo to the top; the photo most in view is
  // aria-current. Built at load so Dawn's focus trap includes the
  // thumbnails, and rebuilt on open only if the photo set changed (Dawn
  // swaps the modal's photos on a variant change).
  var lbModal = null, lbRail = null, lbSig = "", lbIO = null;
  function lightbox() {
    if (lbModal) return lbModal;
    var op = gallery && gallery.querySelector("modal-opener[data-modal]");
    var m = op ? document.querySelector(op.getAttribute("data-modal")) : null;
    if (!m) return null;
    lbModal = m;
    m.classList.add("perkie-d23-lightbox");
    if ("MutationObserver" in window) {
      new MutationObserver(function () {
        if (m.hasAttribute("open")) buildRail();
      }).observe(m, { attributes: true, attributeFilter: ["open"] });
    }
    return m;
  }
  function thumbSrc(it) {
    var img = it.tagName === "IMG" ? it : it.querySelector("img");
    var src = img ? (img.getAttribute("src") || "") : "";
    if (!src) return "";
    return src.split("?")[0] + "?width=160";
  }
  function markRail(target) {
    if (!lbRail) return;
    var btns = lbRail.children;
    for (var i = 0; i < btns.length; i++) {
      var on = btns[i]._perkieTarget === target;
      if (on !== (btns[i].getAttribute("aria-current") === "true")) {
        if (on) btns[i].setAttribute("aria-current", "true");
        else btns[i].removeAttribute("aria-current");
      }
      if (on) {
        var r = lbRail.getBoundingClientRect(), b = btns[i].getBoundingClientRect();
        if (b.top < r.top) lbRail.scrollTop += b.top - r.top;
        else if (b.bottom > r.bottom) lbRail.scrollTop += b.bottom - r.bottom;
      }
    }
  }
  function buildRail() {
    if (!isWide()) return;
    var m = lightbox();
    var content = m && m.querySelector(".product-media-modal__content");
    var dialog = m && m.querySelector(".product-media-modal__dialog");
    if (!content || !dialog) return;
    var all = content.children, shown = [], ids = [];
    for (var i = 0; i < all.length; i++) {
      if (!all[i].hasAttribute("data-media-id")) continue;
      if (window.getComputedStyle(all[i]).display === "none") continue;
      shown.push(all[i]);
      ids.push(all[i].getAttribute("data-media-id"));
    }
    if (!lbRail) {
      lbRail = document.createElement("div");
      lbRail.className = "perkie-d23-rail";
      lbRail.setAttribute("role", "group");
      lbRail.setAttribute("aria-label", "Photos");
    }
    if (lbRail.parentNode !== dialog) dialog.appendChild(lbRail);
    var sig = ids.join("|");
    if (sig !== lbSig) {
      lbSig = sig;
      lbRail.textContent = "";
      for (var k = 0; k < shown.length; k++) {
        (function (it, n) {
          var b = document.createElement("button");
          b.type = "button";
          b.className = "perkie-d23-rail__thumb";
          b.setAttribute("aria-label", "Photo " + (n + 1) + " of " + shown.length);
          var t = document.createElement("img");
          t.alt = "";
          t.decoding = "async";
          t.src = thumbSrc(it);
          b.appendChild(t);
          b._perkieTarget = it;
          b.addEventListener("click", function () {
            it.scrollIntoView({ block: "start" });
            markRail(it);
          });
          lbRail.appendChild(b);
        })(shown[k], k);
      }
      if (lbIO) lbIO.disconnect();
      lbIO = null;
      if ("IntersectionObserver" in window) {
        var thresholds = [];
        for (var q = 0; q <= 20; q++) thresholds.push(q / 20);
        lbIO = new IntersectionObserver(function (entries) {
          for (var e = 0; e < entries.length; e++) {
            entries[e].target._perkieSeen = entries[e].isIntersecting ? entries[e].intersectionRect.height : 0;
          }
          var best = null, bestH = 0;
          for (var x = 0; x < shown.length; x++) {
            var h = shown[x]._perkieSeen || 0;
            if (h > bestH) { bestH = h; best = shown[x]; }
          }
          if (best) markRail(best);
        }, { root: content, threshold: thresholds });
        for (var o = 0; o < shown.length; o++) lbIO.observe(shown[o]);
      }
    }
    var active = content.querySelector(".active[data-media-id]");
    if (active) markRail(active);
  }

  /* ── render ──────────────────────────────────────────────────────── */
  function renderPlate() {
    var chosen = isChosen();
    var art = (chosen && chosenArt()) || s.art;
    // Below 750 nothing changes: no tile in Dawn's mobile strip, Dawn's own
    // thumbnail selection untouched (the overlay is display:none there).
    var wide = isWide();
    ensureTile(wide ? art : null);
    var on = !!(wide && art && s.showArt);
    if (art) setPlateArt(art);
    if (plate) {
      plate.classList.toggle("is-on", on);
      // The caption, as the artifact's artLabel draws it: names joined by
      // " + ", then the style; at the handover (art up, no style chosen) the
      // style of the art shown plus " · Preview". Names drop when any pet is
      // unnamed (never "Pet 1"); the style always reads.
      var text = "";
      var label = chosen ? (s.chosen.styleLabel || "") : s.artLabel;
      if (art && label) {
        var n = currentNames();
        text = (namedAll(n) ? n.join(" + ") + " · " : "") + label + (chosen ? "" : " · Preview");
      }
      if (cap.textContent !== text) cap.textContent = text;
      if (cap.hidden !== !text) cap.hidden = !text;
    }
    renderSwap(wide ? art : null, on);
    syncSelection(on);
  }

  /* ── wide: "Show N more photos" loads photos 10+ into the run ───────── */
  // Corey 2026-10-11: no full-screen page - the button adds
  // .perkie-d23--all-photos to the root (it survives Dawn's gallery swap on a
  // variant change) and the CSS shows the run's remaining tiles two-up. N is
  // read from the run itself (hide_variants keeps the other variants' photos
  // out of it), so it stays right after a variant change. Hidden at 9 or
  // fewer, and once the grid is open.
  function syncSeeAll() {
    if (!seeAll) return;
    var v = viewer();
    var items = v ? v.querySelectorAll(".product__media-list > li[data-media-id]") : [];
    var more = items.length - 9;
    var btn = seeAll.querySelector("[data-perkie-d23-more]");
    if (btn && more > 0) {
      var label = "Show " + more + " more photo" + (more === 1 ? "" : "s");
      if (btn.textContent.trim() !== label) btn.textContent = label;
    }
    var hide = more < 1 || root.classList.contains("perkie-d23--all-photos");
    if (seeAll.hidden !== hide) seeAll.hidden = hide;
  }

  root.addEventListener("click", function (e) {
    var btn = e.target && e.target.closest && e.target.closest("[data-perkie-d23-more]");
    if (!btn) return;
    var v = viewer();
    var tenth = v ? v.querySelectorAll(".product__media-list > li[data-media-id]")[9] : null;
    root.classList.add("perkie-d23--all-photos");
    syncSeeAll();
    // The button is gone: keyboard focus lands on the first new photo.
    var target = tenth && tenth.querySelector("button, a[href], [tabindex]");
    if (target) target.focus({ preventScroll: true });
  });

  function renderPersonal() {
    if (!personal) return;
    var chosen = isChosen();
    var art = (chosen && chosenArt()) || s.art;
    var n = currentNames();
    var text = "";
    // FP1 (A4): from the wait's first frame (step 4), not the first art, so
    // the title never grows under a tile she is about to tap.
    if ((art || s.step === 4) && namedAll(n)) {
      text = "of " + listNames(n);
      if (chosen && s.chosen.styleLabel) text += ", " + s.chosen.styleLabel;
    }
    if (personal.textContent !== text) personal.textContent = text;
    if (personal.hidden !== !text) personal.hidden = !text;
  }

  // Port Step 48-E (PM-298 A.2): the fix line names the pet once the card knows
  // every pet's name (the personal line's names and join: "Bella", "Bella and
  // Ollie"); otherwise, and after a reset, the span's own no-name words.
  // 48-F (PM-300): every fix line on the page (standard and memorial; the CSS
  // shows one register); two or more pets not all named take the span's
  // "many" words ("your pets"; memorial's "them" is the same either way).
  function renderFix() {
    if (!fixNames.length) return;
    var n = currentNames();
    var named = namedAll(n) ? listNames(n) : "";
    for (var i = 0; i < fixNames.length; i++) {
      var f = fixNames[i];
      var text = named || (n.length > 1 ? f.many : f.one);
      if (f.el.textContent !== text) f.el.textContent = text;
    }
  }

  var pinRaf = 0;
  function schedulePin() {
    cancelAnimationFrame(pinRaf);
    pinRaf = requestAnimationFrame(function () {
      updateHold();
      if (pin) updatePin();
    });
  }
  // BET 01: pins only once a style is chosen, and only while the block fits
  // under the 112px offset; otherwise it stays static. No animation.
  function updatePin() {
    var h = pin.offsetHeight;
    var on = isWide() && isChosen() && h > 0 && h <= window.innerHeight - PIN_TOP;
    if (on !== pin.hasAttribute("data-perkie-d23-pinned")) {
      if (on) pin.setAttribute("data-perkie-d23-pinned", "");
      else pin.removeAttribute("data-perkie-d23-pinned");
    }
  }

  // FLAG D23-08: wide only, before a style is chosen. The column holds at
  // its bottom edge: top = min(112, viewportH - columnH - 16). No animation.
  function updateHold() {
    if (!column) return;
    var on = isXWide() && !isChosen();
    if (on !== column.hasAttribute("data-perkie-d23-hold")) {
      if (on) column.setAttribute("data-perkie-d23-hold", "");
      else {
        column.removeAttribute("data-perkie-d23-hold");
        column.style.removeProperty("--perkie-d23-hold-top");
      }
    }
    if (!on) return;
    var top = Math.round(Math.min(PIN_TOP, window.innerHeight - column.offsetHeight - HOLD_GAP)) + "px";
    if (column.style.getPropertyValue("--perkie-d23-hold-top") !== top) column.style.setProperty("--perkie-d23-hold-top", top);
  }

  var busy = false;
  function render() {
    busy = true;
    try {
      renderPlate();
      renderPersonal();
      renderFix();
    } finally {
      busy = false;
    }
    schedulePin();
  }

  /* ── listeners ───────────────────────────────────────────────────── */
  document.addEventListener("perkie:flow3c-state", function (e) {
    var d = e && e.detail;
    if (!d || d.mount !== "pdp") return;
    if (mount && e.target && e.target !== document && e.target.nodeType === 1 && !mount.contains(e.target)) return;
    var had = !!s.art;
    s.art = typeof d.previewUrl === "string" && d.previewUrl ? d.previewUrl : null;
    s.artLabel = s.art && typeof d.previewStyleLabel === "string" ? d.previewStyleLabel : "";
    s.names = Array.isArray(d.names) ? d.names : [];
    s.step = typeof d.step === "number" ? d.step : 0;
    if (!had && s.art) s.showArt = true; // R1: the handover at the first preview
    if (!s.art && !isChosen()) s.showArt = false;
    render();
  });

  window.addEventListener("perkie:progress", function (e) {
    var st = e && e.detail && e.detail.stage;
    if (!st) return;
    s.stage = st;
    render();
  });

  window.addEventListener("perkie:style-selected", function (e) {
    var d = e && e.detail;
    if (!d || !d.style) return;
    var prev = s.chosen && s.chosen.style;
    s.chosen = d;
    // A NEW style choice lands on the plate even if a product photo was
    // up; a re-broadcast of the same style (names edited) does not yank it.
    if (d.style !== prev) s.showArt = true;
    render();
  });

  window.addEventListener("perkie:previewer-reset", function () {
    s.chosen = null;
    render();
  });

  // Strip taps: "Your art" brings the art back; a product thumbnail hands
  // the plate to Dawn (its own click handler sets the active media).
  if (gallery) {
    gallery.addEventListener("click", function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      if (t.closest("[data-perkie-d23-art-thumb]")) {
        s.showArt = true;
        render();
        return;
      }
      var item = t.closest("[data-target]");
      if (item && thumbList() && thumbList().contains(item)) {
        s.showArt = false;
        render();
      }
    });
    // Dawn replaces the thumbnail rail on a variant change (product-info.js
    // updateMedia) and re-marks aria-current: put the tile back and re-assert
    // the selection. Our own writes are ignored while render() runs.
    if ("MutationObserver" in window) {
      var moRaf = 0;
      new MutationObserver(function () {
        if (busy) return;
        cancelAnimationFrame(moRaf);
        moRaf = requestAnimationFrame(function () {
          syncSeeAll();
          syncBar();
          if (s.art || s.chosen) render();
        });
      }).observe(gallery, { childList: true, subtree: true, attributes: true, attributeFilter: ["aria-current"] });
    }
  }

  window.addEventListener("resize", schedulePin);
  window.addEventListener("resize", paintBar);
  if (wideMql) {
    var onWide = function () { render(); };
    if (wideMql.addEventListener) wideMql.addEventListener("change", onWide);
    else if (wideMql.addListener) wideMql.addListener(onWide);
  }
  if (xwMql) {
    if (xwMql.addEventListener) xwMql.addEventListener("change", onWide);
    else if (xwMql.addListener) xwMql.addListener(onWide);
  }
  if ("ResizeObserver" in window) {
    var ro = new ResizeObserver(schedulePin);
    if (pin) ro.observe(pin);
    // The card grows as it advances: the D23-08 hold re-reads the column.
    if (column) ro.observe(column);
  }

  // The band's "Read all reviews" targets #reviews. Since port step 39 an
  // id="reviews" anchor sits directly above the Junip widget (sections/
  // perkie-pdp-below.liquid), so on product.custom.json this fallback does
  // not run; it stays for a template without that section, where it points
  // the link at the Junip review app block (debt D-S38-REVIEWS-ANCHOR).
  var reviewsLink = root.querySelector("[data-perkie-d23-reviews-link]");
  if (reviewsLink && !document.getElementById("reviews")) {
    var junip = document.querySelector('[id^="shopify-block-"][id*="junip_product_review"]') ||
      document.querySelector('[id*="junip_junip_product_review"]');
    if (junip && junip.id) reviewsLink.setAttribute("href", "#" + junip.id);
  }

  syncSeeAll();
  syncBar();
  lightbox();
  buildRail();
  render();
})();
